import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { documentApi, fileApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import Spinner from '../../components/Spinner.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import { formatCurrency, formatDate } from '../../utils/format.js';

/**
 * The printable document.
 *
 * Rendered on its own, outside the app shell: no sidebar, no toolbar, white
 * page. "Print" opens the browser dialog, which is also how you get a PDF -
 * that avoids shipping a PDF engine and keeps the layout identical on screen
 * and on paper. The stylesheet lives in styles/print.css.
 */
const DocumentPrintPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;

  const { data: doc, loading, error } = useApi(() => documentApi.get(id), [id]);

  useEffect(() => {
    if (doc) document.title = doc.number;
  }, [doc]);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={error.message} />;

  const company = doc.company ?? {};
  const customer = doc.customerSnapshot ?? {};
  const totals = doc.totals;
  const money = (value) => formatCurrency(value, locale, doc.currency);
  const accent = company.accentColor || '#2f5bea';
  const logo = fileApi.url(company.logoFileId);

  const dateLabel = { QUOTE: t('documents.validUntil'), INVOICE: t('documents.dueDate') }[doc.type];
  const dateValue = doc.type === 'QUOTE' ? doc.validUntil : doc.dueDate;

  return (
    <div className="sheet" style={{ '--accent': accent }}>
      <div className="sheet__toolbar no-print">
        <button type="button" className="btn btn--primary" onClick={() => window.print()}>
          {t('documents.print')}
        </button>
        <button type="button" className="btn" onClick={() => window.close()}>
          {t('common.close')}
        </button>
        <span className="muted small">{t('documents.printHint')}</span>
      </div>

      <article className="page">
        <header className="page__head">
          <div className="page__brand">
            {logo ? <img className="page__logo" src={logo} alt={company.name || ''} /> : null}
            <div>
              <p className="page__company">{company.name}</p>
              <p className="page__company-meta">
                {[company.address, `${company.postalCode ?? ''} ${company.city ?? ''}`.trim(), company.country]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="page__company-meta">
                {[company.phone, company.email, company.website].filter(Boolean).join(' · ')}
              </p>
              {company.vatNumber ? (
                <p className="page__company-meta">
                  {t('documents.vatNumber')}: {company.vatNumber}
                </p>
              ) : null}
            </div>
          </div>

          <div className="page__title">
            <h1>{t(`documents.types.${doc.type}`)}</h1>
            <p className="page__number">{doc.number}</p>
            <p className="page__date">
              {t('documents.issueDate')}: {formatDate(doc.issueDate, locale)}
            </p>
            {dateValue ? (
              <p className="page__date">
                {dateLabel}: {formatDate(dateValue, locale)}
              </p>
            ) : null}
          </div>
        </header>

        <section className="page__parties">
          <div className="page__party">
            <p className="page__party-label">{t('documents.billTo')}</p>
            <p className="page__party-name">{customer.companyName ?? doc.customerName}</p>
            {customer.contactName ? <p>{customer.contactName}</p> : null}
            {customer.address ? <p>{customer.address}</p> : null}
            <p>{[customer.city, customer.country].filter(Boolean).join(', ')}</p>
            {customer.email ? <p>{customer.email}</p> : null}
            {customer.vatNumber ? (
              <p>
                {t('documents.vatNumber')}: {customer.vatNumber}
              </p>
            ) : null}
          </div>
        </section>

        <table className="page__lines">
          <thead>
            <tr>
              <th className="col-desc">{t('documents.designation')}</th>
              <th className="col-num">{t('documents.quantity')}</th>
              <th className="col-num">{t('documents.unitPrice')}</th>
              <th className="col-num">%</th>
              <th className="col-num">{t('documents.vat')}</th>
              <th className="col-num">{t('documents.lineTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {totals.lines.map((line) => (
              <tr key={line.position}>
                <td>
                  <span className="line-name">{line.name}</span>
                  {line.description ? <span className="line-desc">{line.description}</span> : null}
                </td>
                <td className="col-num">
                  {line.quantity} {line.unit !== 'unit' ? t(`documents.units.${line.unit}`, line.unit) : ''}
                </td>
                <td className="col-num">{money(line.unitPrice)}</td>
                <td className="col-num">{line.discountPercent ? `${line.discountPercent}%` : '-'}</td>
                <td className="col-num">{line.vatRate}%</td>
                <td className="col-num col-strong">{money(line.netAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="page__totals">
          <table>
            <tbody>
              <tr>
                <td>{t('documents.subtotal')}</td>
                <td className="col-num">{money(totals.subtotal)}</td>
              </tr>
              {totals.discountAmount > 0 ? (
                <tr>
                  <td>{t('documents.discount')}</td>
                  <td className="col-num">-{money(totals.discountAmount)}</td>
                </tr>
              ) : null}
              <tr>
                <td>{t('documents.netAmount')}</td>
                <td className="col-num">{money(totals.netAmount)}</td>
              </tr>
              {totals.vatBreakdown.map((vat) => (
                <tr key={vat.rate}>
                  <td>
                    {t('documents.vat')} {vat.rate}% ({t('documents.on')} {money(vat.base)})
                  </td>
                  <td className="col-num">{money(vat.amount)}</td>
                </tr>
              ))}
              <tr className="grand">
                <td>{t('documents.totalDue')}</td>
                <td className="col-num">{money(totals.total)}</td>
              </tr>
              {doc.type === 'INVOICE' && Number(doc.paidAmount) > 0 ? (
                <>
                  <tr>
                    <td>{t('documents.paid')}</td>
                    <td className="col-num">-{money(doc.paidAmount)}</td>
                  </tr>
                  <tr className="grand">
                    <td>{t('documents.balance')}</td>
                    <td className="col-num">{money(Number(doc.total) - Number(doc.paidAmount))}</td>
                  </tr>
                </>
              ) : null}
              {doc.type === 'RECEIPT' ? (
                <tr className="grand grand--paid">
                  <td colSpan={2}>{t('documents.paidInFull')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>

        {doc.notes ? (
          <section className="page__block">
            <h2>{t('common.notes')}</h2>
            <p>{doc.notes}</p>
          </section>
        ) : null}

        {doc.terms ? (
          <section className="page__block">
            <h2>{t('documents.terms')}</h2>
            <p>{doc.terms}</p>
          </section>
        ) : null}

        {company.iban ? (
          <section className="page__block">
            <h2>{t('documents.payment')}</h2>
            <p>
              IBAN {company.iban}
              {company.bic ? ` · BIC ${company.bic}` : ''}
            </p>
          </section>
        ) : null}

        <footer className="page__foot">
          <p>
            {[company.name, company.legalForm, company.registrationNumber, company.vatNumber]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {company.legalMentions ? <p>{company.legalMentions}</p> : null}
        </footer>
      </article>
    </div>
  );
};

export default DocumentPrintPage;
